import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkGetLoyaltyCouponOptions,
  BulkGetLoyaltyCouponResponse,
  CouponCreatedEnvelope,
  CouponDeletedEnvelope,
  GetCurrentMemberCouponsResponse,
  LoyaltyCoupon,
  LoyaltyCouponQuery,
  LoyaltyCouponsQueryBuilder,
  QueryLoyaltyCouponResponse,
  RedeemCurrentMemberPointsForCouponResponse,
  RedeemPointsForCouponOptions,
  RedeemPointsForCouponResponse,
  bulkGetLoyaltyCoupon as universalBulkGetLoyaltyCoupon,
  deleteLoyaltyCoupon as universalDeleteLoyaltyCoupon,
  getCurrentMemberCoupons as universalGetCurrentMemberCoupons,
  getLoyaltyCoupon as universalGetLoyaltyCoupon,
  queryLoyaltyCoupons as universalQueryLoyaltyCoupons,
  redeemCurrentMemberPointsForCoupon as universalRedeemCurrentMemberPointsForCoupon,
  redeemPointsForCoupon as universalRedeemPointsForCoupon,
  typedQueryLoyaltyCoupons as universalTypedQueryLoyaltyCoupons,
} from './loyalty-v1-coupon-coupons.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function redeemPointsForCoupon(
  httpClient: HttpClient
): RedeemPointsForCouponSignature {
  return (
    rewardId: string,
    options: NonNullablePaths<
      RedeemPointsForCouponOptions,
      `loyaltyAccountId`,
      2
    >
  ) =>
    universalRedeemPointsForCoupon(
      rewardId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface RedeemPointsForCouponSignature {
  /**
   * Redeems a customer's loyalty points for a loyalty reward and creates a loyalty coupon.
   *
   * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
   * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
   * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
   *
   * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
   * @param - ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   */
  (
    rewardId: string,
    options: NonNullablePaths<
      RedeemPointsForCouponOptions,
      `loyaltyAccountId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      RedeemPointsForCouponResponse,
      | `coupon._id`
      | `coupon.accountId`
      | `coupon.memberIdDeprecated`
      | `coupon.couponReference.couponId`
      | `coupon.couponReference.code`
      | `coupon.couponReference.specification.moneyOffAmount`
      | `coupon.couponReference.specification.percentOffRate`
      | `coupon.couponReference.specification.freeShipping`
      | `coupon.couponReference.specification.fixedPriceAmount`
      | `coupon.couponReference.specification.buyXGetY.x`
      | `coupon.couponReference.specification.buyXGetY.y`
      | `coupon.couponReference.specification.scope.namespace`
      | `coupon.couponReference.specification.type`
      | `coupon.status`
      | `coupon.rewardName`,
      6
    >
  >;
}

export function redeemCurrentMemberPointsForCoupon(
  httpClient: HttpClient
): RedeemCurrentMemberPointsForCouponSignature {
  return (rewardId: string) =>
    universalRedeemCurrentMemberPointsForCoupon(
      rewardId,
      // @ts-ignore
      { httpClient }
    );
}

interface RedeemCurrentMemberPointsForCouponSignature {
  /**
   * Redeems a current customer's loyalty points for a loyalty reward and creates a loyalty coupon.
   *
   * Creating a loyalty coupon also creates a corresponding "reference" coupon with the [Coupons API](https://dev.wix.com/docs/rest/business-management/marketing/coupons/coupons/coupon-object).
   * The customer receives the reference coupon, which they can apply to their order. The loyalty coupon and its corresponding reference coupon
   * are linked and the loyalty coupon's `status` reflects the current state of the reference coupon.
   *
   * Check which loyalty rewards a site has available with List Rewards in the Loyalty Rewards API.
   *
   * >**Note:**
   * >This endpoint requires [visitor or member authentication](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities).
   * @param - ID of the loyalty reward to redeem. See the Loyalty Rewards API for more information.
   */
  (rewardId: string): Promise<
    NonNullablePaths<
      RedeemCurrentMemberPointsForCouponResponse,
      | `coupon._id`
      | `coupon.accountId`
      | `coupon.memberIdDeprecated`
      | `coupon.couponReference.couponId`
      | `coupon.couponReference.code`
      | `coupon.couponReference.specification.moneyOffAmount`
      | `coupon.couponReference.specification.percentOffRate`
      | `coupon.couponReference.specification.freeShipping`
      | `coupon.couponReference.specification.fixedPriceAmount`
      | `coupon.couponReference.specification.buyXGetY.x`
      | `coupon.couponReference.specification.buyXGetY.y`
      | `coupon.couponReference.specification.scope.namespace`
      | `coupon.couponReference.specification.type`
      | `coupon.status`
      | `coupon.rewardName`,
      6
    >
  >;
}

export function getLoyaltyCoupon(
  httpClient: HttpClient
): GetLoyaltyCouponSignature {
  return (loyaltyCouponId: string) =>
    universalGetLoyaltyCoupon(
      loyaltyCouponId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLoyaltyCouponSignature {
  /**
   * Retrieves a loyalty coupon.
   * @param - ID of the loyalty coupon to retrieve.
   * @returns Retrieved loyalty coupon.
   */
  (loyaltyCouponId: string): Promise<
    NonNullablePaths<
      LoyaltyCoupon,
      | `_id`
      | `accountId`
      | `memberIdDeprecated`
      | `couponReference.couponId`
      | `couponReference.code`
      | `couponReference.specification.moneyOffAmount`
      | `couponReference.specification.percentOffRate`
      | `couponReference.specification.freeShipping`
      | `couponReference.specification.fixedPriceAmount`
      | `couponReference.specification.buyXGetY.x`
      | `couponReference.specification.buyXGetY.y`
      | `couponReference.specification.scope.namespace`
      | `couponReference.specification.type`
      | `status`
      | `rewardName`,
      5
    >
  >;
}

export function bulkGetLoyaltyCoupon(
  httpClient: HttpClient
): BulkGetLoyaltyCouponSignature {
  return (options?: BulkGetLoyaltyCouponOptions) =>
    universalBulkGetLoyaltyCoupon(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkGetLoyaltyCouponSignature {
  /**
   * Retrieves a sequence of loyalty coupons.
   */
  (options?: BulkGetLoyaltyCouponOptions): Promise<
    NonNullablePaths<
      BulkGetLoyaltyCouponResponse,
      `couponsInSite` | `couponsInSite.${number}.metaSiteId`,
      4
    >
  >;
}

export function getCurrentMemberCoupons(
  httpClient: HttpClient
): GetCurrentMemberCouponsSignature {
  return () =>
    universalGetCurrentMemberCoupons(
      // @ts-ignore
      { httpClient }
    );
}

interface GetCurrentMemberCouponsSignature {
  /**
   * Retrieves the loyalty coupons for the currently logged-in member.
   *
   * >**Note:**
   * >This method requires [visitor or member authentication](https://dev.wix.com/docs/api-reference/articles/authentication/about-identities).
   */
  (): Promise<
    NonNullablePaths<
      GetCurrentMemberCouponsResponse,
      | `loyaltyCoupons`
      | `loyaltyCoupons.${number}._id`
      | `loyaltyCoupons.${number}.accountId`
      | `loyaltyCoupons.${number}.memberIdDeprecated`
      | `loyaltyCoupons.${number}.couponReference.couponId`
      | `loyaltyCoupons.${number}.couponReference.code`
      | `loyaltyCoupons.${number}.couponReference.specification.moneyOffAmount`
      | `loyaltyCoupons.${number}.couponReference.specification.percentOffRate`
      | `loyaltyCoupons.${number}.couponReference.specification.freeShipping`
      | `loyaltyCoupons.${number}.couponReference.specification.fixedPriceAmount`
      | `loyaltyCoupons.${number}.couponReference.specification.type`
      | `loyaltyCoupons.${number}.status`
      | `loyaltyCoupons.${number}.rewardName`,
      6
    >
  >;
}

export function queryLoyaltyCoupons(
  httpClient: HttpClient
): QueryLoyaltyCouponsSignature {
  return () =>
    universalQueryLoyaltyCoupons(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLoyaltyCouponsSignature {
  /**
   * Retrieves a list of loyalty coupons, with the specified paging, filtering, and sorting.
   */
  (): LoyaltyCouponsQueryBuilder;
}

export function typedQueryLoyaltyCoupons(
  httpClient: HttpClient
): TypedQueryLoyaltyCouponsSignature {
  return (query: LoyaltyCouponQuery) =>
    universalTypedQueryLoyaltyCoupons(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLoyaltyCouponsSignature {
  /** */
  (query: LoyaltyCouponQuery): Promise<
    NonNullablePaths<
      QueryLoyaltyCouponResponse,
      | `loyaltyCoupons`
      | `loyaltyCoupons.${number}._id`
      | `loyaltyCoupons.${number}.accountId`
      | `loyaltyCoupons.${number}.memberIdDeprecated`
      | `loyaltyCoupons.${number}.couponReference.couponId`
      | `loyaltyCoupons.${number}.couponReference.code`
      | `loyaltyCoupons.${number}.couponReference.specification.moneyOffAmount`
      | `loyaltyCoupons.${number}.couponReference.specification.percentOffRate`
      | `loyaltyCoupons.${number}.couponReference.specification.freeShipping`
      | `loyaltyCoupons.${number}.couponReference.specification.fixedPriceAmount`
      | `loyaltyCoupons.${number}.couponReference.specification.type`
      | `loyaltyCoupons.${number}.status`
      | `loyaltyCoupons.${number}.rewardName`,
      6
    >
  >;
}

export function deleteLoyaltyCoupon(
  httpClient: HttpClient
): DeleteLoyaltyCouponSignature {
  return (_id: string, revision: string) =>
    universalDeleteLoyaltyCoupon(
      _id,
      revision,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteLoyaltyCouponSignature {
  /**
   * Deletes a loyalty coupon.
   *
   * The deletion of a loyalty coupon does not impact the functionality of the corresponding coupon itself.
   * @param - ID of the loyalty coupon to delete.
   * @param - Revision number, which increments by 1 each time the loyalty coupon is updated.
   *
   * To prevent conflicting changes, the current `revision` must be passed when updating the loyalty coupon.
   */
  (_id: string, revision: string): Promise<void>;
}

export const onCouponCreated = EventDefinition(
  'wix.loyalty.v1.coupon_created',
  true,
  (event: CouponCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.couponReference.specification.moneyOffAmount' },
            { path: 'entity.couponReference.specification.percentOffRate' },
            { path: 'entity.couponReference.specification.fixedPriceAmount' },
            { path: 'entity.couponReference.specification.minimumSubtotal' },
          ],
        },
      ])
    )
)<CouponCreatedEnvelope>();
export const onCouponDeleted = EventDefinition(
  'wix.loyalty.v1.coupon_deleted',
  true,
  (event: CouponDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.couponReference.specification.moneyOffAmount' },
            { path: 'undefined.couponReference.specification.percentOffRate' },
            {
              path: 'undefined.couponReference.specification.fixedPriceAmount',
            },
            { path: 'undefined.couponReference.specification.minimumSubtotal' },
          ],
        },
      ])
    )
)<CouponDeletedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  BulkGetLoyaltyCouponOptions,
  BulkGetLoyaltyCouponRequest,
  BulkGetLoyaltyCouponResponse,
  BuyXGetY,
  CouponCreatedEnvelope,
  CouponDeletedEnvelope,
  CouponReference,
  CouponsInSite,
  CursorPaging,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteLoyaltyCouponRequest,
  DeleteLoyaltyCouponResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetCurrentMemberCouponsRequest,
  GetCurrentMemberCouponsResponse,
  GetLoyaltyCouponRequest,
  GetLoyaltyCouponResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  LoyaltyCoupon,
  LoyaltyCouponQuerySpec,
  LoyaltyCouponsQueryBuilder,
  LoyaltyCouponsQueryResult,
  MessageEnvelope,
  Paging,
  PagingMetadataV2,
  QueryLoyaltyCouponRequest,
  QueryLoyaltyCouponResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RedeemCurrentMemberPointsForCouponRequest,
  RedeemCurrentMemberPointsForCouponResponse,
  RedeemMemberPointsForDiscountAmountCouponRequest,
  RedeemMemberPointsForDiscountAmountCouponResponse,
  RedeemPointsForCouponOptions,
  RedeemPointsForCouponRequest,
  RedeemPointsForCouponResponse,
  RestoreInfo,
  Scope,
  SortOrder,
  Sorting,
  Specification,
  SpecificationScopeOrMinSubtotalOneOf,
  SpecificationTypeDetailsOneOf,
  Status,
  Type,
  WebhookIdentityType,
  utils,
} from './loyalty-v1-coupon-coupons.universal.js';
